import { bytesToBase64 } from '../../utils/base64';

export const KEY_FILE_TYPE = 'greenwiser-kiosk-release-key';
export const PBKDF2_ITERATIONS = 1_000_000;
export const MIN_PASSWORD_LENGTH = 16;
export const MAX_PASSWORD_LENGTH = 128;
export const UNSIGNED_KEY_ID_TOKEN = '"key_id":"unsigned"';

type Bytes = Uint8Array<ArrayBuffer>;

const PKCS8_PREFIX = Uint8Array.from([0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20]);
const CHECK_MESSAGE = 'gw-kiosk-release-key-check:v1';
const ADDITIONAL_DATA_LABEL = 'gw-kiosk-release-key:v1';
const FILE_KEYS = ['format', 'type', 'key_id', 'public_key', 'deployment', 'created_at', 'kdf', 'cipher', 'ciphertext'];
const KEY_ID_PATTERN = /^rk-[0-9a-f]{24}$/;
const PRINTABLE_ASCII = /^[\x20-\x7e]*$/;
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
const DEPLOYMENT = /^(https:\/\/[a-z0-9.-]+|http:\/\/(localhost|127\.0\.0\.1))(:[0-9]{1,5})?$/;
const EARLIEST_CREATED_AT = Date.parse('2020-01-01T00:00:00.000Z');
const encoder = new TextEncoder();

export type ReleaseKeyErrorReason =
  | 'password_too_short' | 'password_too_long' | 'password_not_ascii' | 'passwords_differ'
  | 'file_not_json' | 'file_invalid' | 'password_wrong' | 'key_mismatch' | 'ed25519_unavailable'
  | 'placeholder_missing' | 'placeholder_repeated' | 'manifest_not_utf8' | 'crypto_failed' | 'deployment_invalid';

export class ReleaseKeyError extends Error {
  constructor(readonly reason: ReleaseKeyErrorReason) {
    super(reason);
    this.name = 'ReleaseKeyError';
  }
}

export interface SigningKey {
  readonly keyId: string;
  readonly publicKeyB64: string;
  sign(message: Uint8Array): Promise<string>;
}

export interface Ed25519Provider {
  generate(): Promise<{ seed: Bytes; publicKey: Bytes }>;
  importSeed(seed: Uint8Array): Promise<(message: Uint8Array) => Promise<Bytes>>;
  verify(publicKey: Uint8Array, message: Uint8Array, signature: Uint8Array): Promise<boolean>;
}

export interface ReleaseKeyFile {
  format: 1;
  type: typeof KEY_FILE_TYPE;
  key_id: string;
  public_key: string;
  deployment: string;
  created_at: string;
  kdf: { name: 'PBKDF2'; hash: 'SHA-256'; iterations: number; salt: string };
  cipher: { name: 'AES-GCM'; iv: string };
  ciphertext: string;
}

function base64ToBytes(text: string): Bytes {
  const binary = atob(text);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

export const webCryptoEd25519: Ed25519Provider = {
  async generate() {
    let pair: CryptoKeyPair;
    try {
      pair = (await crypto.subtle.generateKey({ name: 'Ed25519' }, true, ['sign', 'verify'])) as CryptoKeyPair;
    } catch {
      throw new ReleaseKeyError('ed25519_unavailable');
    }
    let pkcs8: Bytes | null = null;
    try {
      pkcs8 = new Uint8Array(await crypto.subtle.exportKey('pkcs8', pair.privateKey));
      const publicKey = new Uint8Array(await crypto.subtle.exportKey('raw', pair.publicKey));
      const exported = pkcs8;
      if (exported.length !== 48 || !PKCS8_PREFIX.every((byte, index) => exported[index] === byte)) throw new ReleaseKeyError('ed25519_unavailable');
      return { seed: exported.slice(16), publicKey };
    } catch {
      throw new ReleaseKeyError('ed25519_unavailable');
    } finally {
      if (pkcs8 !== null) pkcs8.fill(0);
    }
  },
  async importSeed(seed) {
    const pkcs8 = new Uint8Array(48);
    pkcs8.set(PKCS8_PREFIX);
    pkcs8.set(seed, 16);
    let key: CryptoKey;
    try {
      key = await crypto.subtle.importKey('pkcs8', pkcs8, { name: 'Ed25519' }, false, ['sign']);
    } catch {
      throw new ReleaseKeyError('ed25519_unavailable');
    } finally {
      pkcs8.fill(0);
      seed.fill(0);
    }
    return async (message) => new Uint8Array(await crypto.subtle.sign({ name: 'Ed25519' }, key, new Uint8Array(message)));
  },
  async verify(publicKey, message, signature) {
    const key = await crypto.subtle.importKey('raw', new Uint8Array(publicKey), { name: 'Ed25519' }, false, ['verify']);
    return crypto.subtle.verify({ name: 'Ed25519' }, key, new Uint8Array(signature), new Uint8Array(message));
  },
};

export async function ed25519Available(): Promise<boolean> {
  try {
    const { seed, publicKey } = await webCryptoEd25519.generate();
    const sign = await webCryptoEd25519.importSeed(seed);
    const check = encoder.encode(CHECK_MESSAGE);
    return await webCryptoEd25519.verify(publicKey, check, await sign(check));
  } catch {
    return false;
  }
}

export function checkNewPassword(password: string, confirm: string): void {
  if (!PRINTABLE_ASCII.test(password)) throw new ReleaseKeyError('password_not_ascii');
  if (password.length < MIN_PASSWORD_LENGTH) throw new ReleaseKeyError('password_too_short');
  if (password.length > MAX_PASSWORD_LENGTH) throw new ReleaseKeyError('password_too_long');
  if (password !== confirm) throw new ReleaseKeyError('passwords_differ');
}

export async function releaseKeyIdOf(publicKey: Uint8Array): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new Uint8Array(publicKey)));
  return `rk-${Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('').slice(0, 24)}`;
}

export function fingerprintOf(keyId: string): string {
  if (!KEY_ID_PATTERN.test(keyId)) throw new Error(`not a release key id: ${keyId}`);
  const hex = keyId.slice(3).toUpperCase();
  return [0, 4, 8, 12, 16, 20].map((at) => hex.slice(at, at + 4)).join(' ');
}

export const createProofMessage = (keyId: string, publicKeyB64: string): Bytes => encoder.encode(`gw-kiosk-release-key-create:v1:${keyId}:${publicKeyB64}`);
export const replaceProofMessage = (keyId: string, publicKeyB64: string): Bytes => encoder.encode(`gw-kiosk-release-key-replace:v1:${keyId}:${publicKeyB64}`);

function isSaneInstant(value: string): boolean {
  if (!ISO_INSTANT.test(value)) return false;
  const time = Date.parse(value);
  return !Number.isNaN(time) && time >= EARLIEST_CREATED_AT && new Date(time).toISOString() === value;
}

function createdAtOf(now: Date): string {
  if (Number.isNaN(now.getTime())) throw new ReleaseKeyError('file_invalid');
  const createdAt = now.toISOString();
  if (!isSaneInstant(createdAt)) throw new ReleaseKeyError('file_invalid');
  return createdAt;
}

async function cryptoStep<T>(work: () => Promise<T>): Promise<T> {
  try {
    return await work();
  } catch {
    throw new ReleaseKeyError('crypto_failed');
  }
}

const additionalData = (keyId: string, publicKeyB64: string, deployment: string, createdAt: string): Bytes =>
  encoder.encode(JSON.stringify([ADDITIONAL_DATA_LABEL, keyId, publicKeyB64, deployment, createdAt]));

async function wrappingKey(password: string, salt: Bytes, usage: KeyUsage): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', encoder.encode(password), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations: PBKDF2_ITERATIONS }, base, { name: 'AES-GCM', length: 256 }, false, [usage]);
}

async function signingKey(provider: Ed25519Provider, seed: Uint8Array, publicKey: Uint8Array): Promise<SigningKey> {
  const sign = await provider.importSeed(seed);
  const publicKeyB64 = bytesToBase64(publicKey);
  const keyId = await releaseKeyIdOf(publicKey);
  const check = encoder.encode(CHECK_MESSAGE);
  if (!(await provider.verify(publicKey, check, await sign(check)))) throw new ReleaseKeyError('key_mismatch');
  return { keyId, publicKeyB64, sign: async (message) => bytesToBase64(await sign(message)) };
}

export async function sealReleaseKeyFile(seed: Uint8Array, publicKey: Uint8Array, password: string, deployment: string, now: Date): Promise<ReleaseKeyFile> {
  if (!DEPLOYMENT.test(deployment)) throw new ReleaseKeyError('deployment_invalid');
  const createdAt = createdAtOf(now);
  const keyId = await cryptoStep(() => releaseKeyIdOf(publicKey));
  const publicKeyB64 = bytesToBase64(publicKey);
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new Uint8Array(seed);
  let ciphertext: Bytes;
  try {
    ciphertext = await cryptoStep(async () => new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: additionalData(keyId, publicKeyB64, deployment, createdAt) }, await wrappingKey(password, salt, 'encrypt'), plaintext)));
  } finally {
    plaintext.fill(0);
  }
  return {
    format: 1,
    type: KEY_FILE_TYPE,
    key_id: keyId,
    public_key: publicKeyB64,
    deployment,
    created_at: createdAt,
    kdf: { name: 'PBKDF2', hash: 'SHA-256', iterations: PBKDF2_ITERATIONS, salt: bytesToBase64(salt) },
    cipher: { name: 'AES-GCM', iv: bytesToBase64(iv) },
    ciphertext: bytesToBase64(ciphertext),
  };
}

export async function createReleaseKey(password: string, confirm: string, deployment: string, now: Date, provider: Ed25519Provider = webCryptoEd25519): Promise<{ file: ReleaseKeyFile; fileName: string; key: SigningKey }> {
  checkNewPassword(password, confirm);
  const { seed, publicKey } = await provider.generate();
  let file: ReleaseKeyFile;
  try {
    file = await sealReleaseKeyFile(seed, publicKey, password, deployment, now);
  } catch (error) {
    seed.fill(0);
    throw error;
  }
  const key = await signingKey(provider, seed, publicKey);
  return { file, fileName: `greenwiser-release-key-${file.key_id}.json`, key };
}

function exactBytes(value: unknown, length: number): Bytes {
  if (typeof value !== 'string') throw new ReleaseKeyError('file_invalid');
  let bytes: Bytes;
  try {
    bytes = base64ToBytes(value);
  } catch {
    throw new ReleaseKeyError('file_invalid');
  }
  if (bytes.length !== length || bytesToBase64(bytes) !== value) throw new ReleaseKeyError('file_invalid');
  return bytes;
}

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);

export async function openReleaseKey(text: string, password: string, provider: Ed25519Provider = webCryptoEd25519): Promise<SigningKey> {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new ReleaseKeyError('file_not_json');
  }
  if (!isRecord(raw)) throw new ReleaseKeyError('file_invalid');
  const keys = Object.keys(raw);
  if (keys.length !== FILE_KEYS.length || !FILE_KEYS.every((key) => keys.includes(key))) throw new ReleaseKeyError('file_invalid');
  const { key_id: keyId, public_key: publicKeyB64, deployment, created_at: createdAt, kdf, cipher } = raw;
  if (raw.format !== 1 || raw.type !== KEY_FILE_TYPE || typeof deployment !== 'string' || !DEPLOYMENT.test(deployment) || typeof createdAt !== 'string' || !isSaneInstant(createdAt)) throw new ReleaseKeyError('file_invalid');
  if (!isRecord(kdf) || Object.keys(kdf).sort().join() !== 'hash,iterations,name,salt' || kdf.name !== 'PBKDF2' || kdf.hash !== 'SHA-256' || kdf.iterations !== PBKDF2_ITERATIONS) throw new ReleaseKeyError('file_invalid');
  if (!isRecord(cipher) || Object.keys(cipher).sort().join() !== 'iv,name' || cipher.name !== 'AES-GCM') throw new ReleaseKeyError('file_invalid');
  const publicKey = exactBytes(publicKeyB64, 32);
  const salt = exactBytes(kdf.salt, 16);
  const iv = exactBytes(cipher.iv, 12);
  const ciphertext = exactBytes(raw.ciphertext, 48);
  if (typeof keyId !== 'string' || keyId !== (await cryptoStep(() => releaseKeyIdOf(publicKey)))) throw new ReleaseKeyError('file_invalid');
  const unwrapping = await cryptoStep(() => wrappingKey(password, salt, 'decrypt'));
  let seed: Bytes;
  try {
    seed = new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv, additionalData: additionalData(keyId, bytesToBase64(publicKey), deployment, createdAt) }, unwrapping, ciphertext));
  } catch {
    throw new ReleaseKeyError('password_wrong');
  }
  if (seed.length !== 32) {
    seed.fill(0);
    throw new ReleaseKeyError('file_invalid');
  }
  return signingKey(provider, seed, publicKey);
}

export function keyFileBlob(file: ReleaseKeyFile): Blob {
  return new Blob([`${JSON.stringify(file, null, 2)}\n`], { type: 'application/json' });
}

export function substituteKeyId(unsignedManifest: Uint8Array, keyId: string): Bytes {
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(unsignedManifest);
  } catch {
    throw new ReleaseKeyError('manifest_not_utf8');
  }
  const parts = text.split(UNSIGNED_KEY_ID_TOKEN);
  if (parts.length === 1) throw new ReleaseKeyError('placeholder_missing');
  if (parts.length > 2) throw new ReleaseKeyError('placeholder_repeated');
  return encoder.encode(parts.join(`"key_id":"${keyId}"`));
}

export function rotationStatementBytes(previous: SigningKey, next: SigningKey, now: Date): Bytes {
  return encoder.encode(JSON.stringify({
    format: 1,
    type: 'kiosk_release_key_rotation',
    previous_key_id: previous.keyId,
    previous_public_key: previous.publicKeyB64,
    new_key_id: next.keyId,
    new_public_key: next.publicKeyB64,
    created_at: now.toISOString(),
  }));
}
